import React from "react";
import Immutable from "immutable";
import ReactTooltip from "react-tooltip";

import * as DashboardUtils from "js/dashboards/utils";
import {fullReloadForTrendChart} from "js/charting/trend/data-loader";
import ConfigurableTrendChart from "js/charting/trend/configurable-trend-chart";
import * as Rata from "js/common/utils/remote-data";
import * as RatioRepo from "js/common/repo/ratio-repo";
import DataConfigToolbar from "js/dashboards/data-config-toolbar";

import {between} from "js/common/utils/numbers";
import moment from "moment";
import {getThemeColorsList} from "js/common/colors-list";
import DataAggregationPicker from "js/charting/trend/data-aggregation-picker";
import {getDataAggregations} from "js/charting/trend/data-aggregator";
import {RadioGroup as RadioButtonGroup} from "@mui/material";
import RadioButton from "js/common/views/inputs/radio-button";
import RatioForm from "js/dashboards/components/trends/ratio-form";
import MetricForm from "js/dashboards/components/trends/metric-form";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import Checkbox from "js/common/views/inputs/checkbox";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import {createCommonConfig} from "js/dashboards/ai-assistant/utils";

const Trend = React.memo(({
  componentId,
  dimensions,
  data,
  config
}) => {
  const trendConfig = React.useMemo(() => {
    return {
      timeframe: config.get("timeframe").toJS(),
      dataAggregation: config.get("dataAggregation"),
      trendType: "ADVANCED",
      dateDisplay: config.get("compareAgainstPreviousPeriod") ? "RELATIVE" : "ABSOLUTE"
    };
  }, [config]);
  const erroringTrends = Rata.getValue(data).trends.filter(trend => trend.error);
  const {theme} = React.useContext(CustomThemeContext);
  const errorTooltipId = `tooltip-error-${componentId}`;
  const trendType = config.get("trendValueType") || "metric";
  return (
      <>
        {erroringTrends.length > 0 && <div
            style={{
              background: theme.palette.error.background,
              color: theme.palette.error.main,
              marginLeft: 10,
              display: "flex",
              zIndex: 999,
              alignItems: "center",
              borderRadius: 3,
              padding: 5,
              width: "calc(100% - 20px)"
            }}>
          <i className="bhi-caution" />
          <ul style={{margin: "0 0 0 5px", padding: 0}}>
            <li style={{fontSize: 13, listStyle: "none"}}>
              <span
                  data-for={errorTooltipId}
                  data-tip=""
                  style={{
                    display: "inline",
                    whiteSpace: "nowrap",
                    borderBottom: "1px dashed red"
                  }}>{erroringTrends.length} {trendType}{erroringTrends.length > 1 ? "s" : ""}</span>
              <span style={{display: "inline"}}>&nbsp;cannot be displayed on the trend chart. Please select different {trendType}s.</span>
            </li>
            <ReactTooltip
                textColor={theme.palette.text.inverted}
                backgroundColor={theme.palette.background.inverted}
                place="bottom"
                effect="solid"
                id={errorTooltipId}>
              <ul style={{margin: 0, padding: 0}}>
                {erroringTrends.map(trend => <li>{trend.error.message}</li>)}
              </ul>
            </ReactTooltip>
          </ul>
        </div>}
        <ConfigurableTrendChart
            config={trendConfig}
            trendType="ADVANCED"
            data={Rata.getValue(data)}
            rangeEnabled={false}
            zoomEnabled={false}
            yAxisEnabled={false}
            exportEnabled={false}
            height={dimensions.height - (erroringTrends.length > 0 ? 30 : 0)}
            width={dimensions.width} />
      </>
  );
});

const TrendEditor = React.memo(({
  component,
  onComponentChange
}) => {
  const dataConfig = component.get("dataConfig");
  const [type, setType] = React.useState(dataConfig.get("trendValueType") || "ratio");
  const onDataConfigChange = React.useCallback(dataConfig => {
    onComponentChange(component.set("dataConfig", dataConfig));
  }, [onComponentChange, component]);


  const aggregation = dataConfig.get("dataAggregation") || getInitialDataAggregation(dataConfig.get("timeframe"));

  return <div style={{overflowY: "auto", height: "calc(100% - 130px)", padding: "0.5rem"}}>
    <DataConfigToolbar config={dataConfig} onConfigChange={onDataConfigChange} />
    <h3 style={{fontSize: "1rem", margin: "1.5rem 0 1rem 0", textTransform: "capitalize"}}>What would you like to
      trend?</h3>
    <RadioButtonGroup
        name="selectionType"
        value={type}
        onChange={e => {
          setType(e.target.value);
          onDataConfigChange(dataConfig.set("trendValueType", e.target.value));
        }}>
      <div style={{display: "flex"}}>
        <RadioButton
            value="metric"
            label="Metrics" />
        <RadioButton
            value="ratio"
            label="Ratios" />
      </div>
    </RadioButtonGroup>
    <h3 style={{fontSize: "1rem", margin: "1.5rem 0 1rem 0", textTransform: "capitalize"}}>{type} Options</h3>
    <div style={{marginBottom: "1rem", display: "flex", flexDirection: "column"}}>
      <Checkbox
          style={{marginTop: 0}}
          label="Show data cumulatively"
          checked={type === "metric" && dataConfig.get("displayDataCumulatively", false)}
          disabled={type === "ratio"}
          onCheck={(e, isChecked) => {
            onDataConfigChange(dataConfig.set("displayDataCumulatively", isChecked));
          }}
      />
      <Checkbox
          style={{marginTop: 0}}
          label="Compare against previous period"
          checked={dataConfig.get("compareAgainstPreviousPeriod", false)}
          onCheck={(e, isChecked) => {
            onDataConfigChange(dataConfig.set("compareAgainstPreviousPeriod", isChecked));
          }}
      />
    </div>
    <div style={{marginBottom: "1rem"}}>
      <DataAggregationPicker
          dataAggregations={getDataAggregations(aggregation)}
          value={aggregation}
          onChange={dataAggregation => onDataConfigChange(dataConfig.set("dataAggregation", dataAggregation))}
          style={{width: 235}} />
    </div>
    {type === "ratio" ? <RatioForm dataConfig={dataConfig} onDataConfigChange={onDataConfigChange} /> : <MetricForm
        dataConfig={dataConfig}
        onDataConfigChange={onDataConfigChange} />}
  </div>;
});

const getInitialDataAggregation = (timeframe) => {
  const start = moment(timeframe.start);
  const end = moment(timeframe.end);
  const noOfDays = end.diff(start, "days") + 1;
  let dataAggregation;

  if (between(noOfDays, 1, 31)) {
    dataAggregation = "DAILY";
  }
  if (between(noOfDays, 31, 180)) {
    dataAggregation = "WEEKLY";
  }
  if (between(noOfDays, 180, 365 * 2)) {
    dataAggregation = "MONTHLY";
  }
  if (between(noOfDays, 365 * 2, 365 * 5)) {
    dataAggregation = "QUARTERLY";
  }
  if (noOfDays > 365 * 5) {
    dataAggregation = "YEARLY";
  }

  return dataAggregation;
};

const getPreviousAndCurrentTimeframeModels = (currentTimeframe) => {
  const timeframeModel = TimeframeRepo.parse(currentTimeframe);
  switch (timeframeModel.get("id")) {
    case "today":
      return [TimeframeRepo.get("yesterday"), timeframeModel]
    case "wtd":
      return [TimeframeRepo.get("last_week"), timeframeModel]
    case "mtd":
      return [TimeframeRepo.get("last_month"), timeframeModel]
    case "qtd":
      return [TimeframeRepo.get("last_quarter"), timeframeModel]
    case "ytd":
      return [TimeframeRepo.get("last_year"), timeframeModel]
    default:
      const startMoment = timeframeModel.get("start");
      const endMoment = timeframeModel.get("end");
      const length = endMoment.diff(startMoment, 'days') + 1;
      const newStartMoment = startMoment.clone().subtract(length, 'days');
      const newEndMoment = endMoment.clone().subtract(length, 'days');

      return [TimeframeRepo.parse({
        type: "custom",
        start: newStartMoment,
        end: newEndMoment
      }),
        TimeframeRepo.parse({
          type: "custom",
          start: startMoment,
          end: endMoment
        })];
  }
};

const getTimeframeName = (timeframeModel) => {
  if (timeframeModel.get("id") === "custom") {
    return `${timeframeModel.get("start").format("L")} - ${timeframeModel.get("end").format("L")}`;
  } else {
    return timeframeModel.get("name");
  }
}

const getDataGroups = config => {
  const dataGroupIndex = 0;
  const howToPlot = config.displayDataCumulatively && config.trendValueType === "metric" ?
      "cumulative" : "non-cumulative";

  const getDefaultColour = (trendIndex, dataGroupIndex = 0) => {
    const coloursList = getThemeColorsList(dataGroupIndex);
    return coloursList[trendIndex % coloursList.length];
  };

  const metricTrendConfigs = config.kpiIds.map((kpiId, index) => {
    return {
      cid: Math.random(),
      type: "KPI",
      kpiId: kpiId,
      whatToPlot: config.averageByUser ? "average-by-user" : "value",
      seriesColour: getDefaultColour(index, dataGroupIndex),
      lineStyle: "Solid",
      dataPointMarker: null,
      howToPlot,
      yAxis: 0
    };
  });

  const ratioTrendConfigs = config.ratiosList.map((r, index) => {
    const ratio = RatioRepo.get(r.id).toJS();
    return {
      cid: Math.random(),
      type: "RATIO",
      ratio: ratio,
      whatToPlot: config.averageByUser ? "average-by-user" : "value",
      seriesColour: getDefaultColour(index, dataGroupIndex),
      lineStyle: "Solid",
      dataPointMarker: null,
      howToPlot,
      yAxis: 0
    };
  });

  const trendConfigs = config.trendValueType === "ratio" ? ratioTrendConfigs : metricTrendConfigs;

  if (config.compareAgainstPreviousPeriod) {
    const [previousTimeframeModel, currentTimeframeModel] = getPreviousAndCurrentTimeframeModels(config.timeframe);
    return [{
      cid: Math.random(),
      name: getTimeframeName(currentTimeframeModel),
      timeframe: currentTimeframeModel.getRawJson(),
      trendConfigs: trendConfigs,
      qualifierType: config.qualifier.type,
      qualifierId: config.qualifier.id,
      clientIds: config.clientFilter.allClientIds,
      matchAnyTagIds: config.tagFilter.matchAnyTagIds,
      matchAllTagIds: config.tagFilter.matchAllTagIds,
      excludedTagIds: config.tagFilter.excludedTagIds
    },
      {
        cid: Math.random(),
        name: getTimeframeName(previousTimeframeModel),
        timeframe: previousTimeframeModel.getRawJson(),
        trendConfigs: trendConfigs.map(trendConfig => Object.assign({}, trendConfig, {lineStyle: "ShortDash"})),
        qualifierType: config.qualifier.type,
        qualifierId: config.qualifier.id,
        clientIds: config.clientFilter.allClientIds,
        matchAnyTagIds: config.tagFilter.matchAnyTagIds,
        matchAllTagIds: config.tagFilter.matchAllTagIds,
        excludedTagIds: config.tagFilter.excludedTagIds
      }]
  }

  return [
    {
      cid: Math.random(),
      name: "",
      timeframe: config.timeframe,
      trendConfigs: trendConfigs,
      qualifierType: config.qualifier.type,
      qualifierId: config.qualifier.id,
      clientIds: config.clientFilter.allClientIds,
      matchAnyTagIds: config.tagFilter.matchAnyTagIds,
      matchAllTagIds: config.tagFilter.matchAllTagIds,
      excludedTagIds: config.tagFilter.excludedTagIds,
      dateDisplay: "ABSOLUTE"
    }];
};

const transformTrendConfig = (backendConfig, dashboardConfig) => {
  const commonConfig = createCommonConfig(backendConfig, dashboardConfig);
  return commonConfig.merge(Immutable.Map({
    kpiIds: backendConfig.get("kpiIds", Immutable.List()),
    ratiosList: backendConfig.get("ratioIds", Immutable.List()).map(ratioId => Immutable.Map({id: ratioId})),
    trendValueType: backendConfig.get("trendValueType"),
    type: "global"
  }));
};

export default Immutable.fromJS({
  type: "Trend",
  label: "Trend",

  previewDimensions: Immutable.Map({
    width: 560,
    height: 327
  }),
  expandedPreviewDimensions: Immutable.Map({
    width: 1120,
    height: 627
  }),

  transformConfig: (backendConfig, dashboardConfig) =>
      transformTrendConfig(backendConfig, dashboardConfig),

  transformData: (data) => {
    return Immutable.Map({
      value: data
    });
  },

  getDefaultData: () => ({isLoading: false, trends: []}),
  getReactComponent: () => Trend,
  getEditorReactComponent: () => TrendEditor,
  getTitle: component => component.get("title"),
  canFullScreen: true,
  variant: "scrollable",
  layout: {
    min: {width: 14, height: 8},
    max: {width: 48, height: 24},
    init: {width: 24, height: 12}
  },
  applyParentConfig: (parentConfig, dataConfig) => DashboardUtils.applyInheritanceForKeys(parentConfig, dataConfig),
  load: component => {
    const config = component.get("dataConfig").toJS();
    const chartConfig = {
      trendType: "ADVANCED",
      dataAggregation: config.dataAggregation || getInitialDataAggregation(config.timeframe),
      dataGroups: getDataGroups(config)
    };
    const {promiseForFinalData} = fullReloadForTrendChart(chartConfig, false, true);
    return promiseForFinalData;
  }
});
